/**
 * Customers Page - Customer List & Email Subscribers
 *
 * Shows customer data from orders and email signups.
 * Merges customers from Square (in-store) and Portal (online).
 * Tammy can add notes and tags (e.g. allergy, VIP) per customer.
 */

import { useState, useEffect, useCallback } from 'react';
import { HiSearch, HiMail, HiUsers, HiDownload, HiRefresh, HiPencil } from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Customer {
  name: string;
  email: string | null;
  phone: string | null;
  address?: Record<string, unknown> | null;
  source: 'online' | 'square';
  square_customer_id?: string;
}

interface CustomerNote {
  notes: string;
  tags: string;
  updated_at?: string;
}

export default function Customers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSubscribersOnly, setShowSubscribersOnly] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState({ total: 0, fromSquare: 0, fromOnline: 0, withEmail: 0 });
  const [loading, setLoading] = useState(true);
  const [notesMap, setNotesMap] = useState<Record<string, CustomerNote>>({});
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/customer-notes`);
      if (res.ok) {
        const data = await res.json();
        setNotesMap(data.notes || {});
      }
    } catch (err) {
      console.error('Error fetching customer notes:', err);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/customers?includeSquare=true`);
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
        setStats(data.stats || { total: 0, fromSquare: 0, fromOnline: 0, withEmail: 0 });
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchNotes();
  }, [fetchCustomers, fetchNotes]);

  const openNoteEditor = (email: string | null) => {
    if (!email) return;
    const key = email.trim().toLowerCase();
    const existing = notesMap[key];
    setEditingEmail(email);
    setEditNotes(existing?.notes ?? '');
    setEditTags(existing?.tags ?? '');
  };

  const saveNote = async () => {
    if (!editingEmail) return;
    setSavingNote(true);
    try {
      const res = await adminFetch(`${API_URL}/api/customer-notes`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          customer_email: editingEmail,
          notes: editNotes.trim(),
          tags: editTags.trim(),
        }),
      });
      if (!res.ok) throw new Error('Failed to save');
      const key = editingEmail.trim().toLowerCase();
      setNotesMap((prev) => ({
        ...prev,
        [key]: { notes: editNotes.trim(), tags: editTags.trim() },
      }));
      setEditingEmail(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save notes');
    } finally {
      setSavingNote(false);
    }
  };

  const getNote = (email: string | null): CustomerNote | undefined =>
    email ? notesMap[email.trim().toLowerCase()] : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
        <p className="text-gray-500 mt-1">
          View your customer list. Add notes or tags (e.g. allergy, VIP) to remember important details.
        </p>
      </div>

      {/* Note editor modal */}
      {editingEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Notes & tags</h3>
            <p className="text-sm text-gray-500 mb-3">{editingEmail}</p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
            <input
              type="text"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              placeholder="e.g. VIP, nut allergy"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-purple-500"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              placeholder="e.g. Prefers pickup after 2pm. Corporate – invoice."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-purple-500"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingEmail(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveNote}
                disabled={savingNote}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {savingNote ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-600 mb-1">
            <HiUsers className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-600">Total Customers</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-blue-600 mb-1">
            <HiMail className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.withEmail}</p>
          <p className="text-sm text-gray-600">With Email</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-green-600 mb-1">
            <HiUsers className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.fromOnline}</p>
          <p className="text-sm text-gray-600">From Online</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-600 mb-1">
            <HiUsers className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.fromSquare}</p>
          <p className="text-sm text-gray-600">From Square</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 
                       focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
        <label className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 
                          rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={showSubscribersOnly}
            onChange={(e) => setShowSubscribersOnly(e.target.checked)}
            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span className="text-sm text-gray-700">Subscribers only</span>
        </label>
        <button
          onClick={fetchCustomers}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white 
                     rounded-lg hover:bg-purple-700 transition-colors"
        >
          <HiRefresh className="w-5 h-5" />
          Refresh
        </button>
        <button
          disabled
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-400 
                     rounded-lg cursor-not-allowed"
        >
          <HiDownload className="w-5 h-5" />
          Export CSV
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiRefresh className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Customers...</h3>
        </div>
      )}

      {/* Empty State */}
      {!loading && customers.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiUsers className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Customers Yet</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Customer information will appear here as they place orders 
            or make purchases in-store (Square).
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto text-left">
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-medium text-purple-900 mb-1">💡 Collect Emails</h4>
              <p className="text-sm text-purple-700">
                Add an email signup form to your website to build your customer list.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-1">📧 Use Mailchimp</h4>
              <p className="text-sm text-blue-700">
                Connect Mailchimp to send newsletters about new products and promotions.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Customer List */}
      {!loading && customers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes / Tags</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers
                  .filter(customer => {
                    const matchesSearch = 
                      !searchQuery ||
                      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      customer.phone?.includes(searchQuery);
                    const matchesSubscribers = !showSubscribersOnly || customer.email;
                    return matchesSearch && matchesSubscribers;
                  })
                  .map((customer, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {customer.email ? (
                          <a href={`mailto:${customer.email}`} className="text-sm text-purple-600 hover:underline">
                            {customer.email}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {customer.phone ? (
                          <a href={`tel:${customer.phone}`} className="text-sm text-purple-600 hover:underline">
                            {customer.phone}
                          </a>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {customer.source === 'square' ? (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                            In-Store
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            Online
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {customer.email ? (
                          <>
                            {(() => {
                              const note = getNote(customer.email);
                              const tagList = (note?.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
                              return (
                                <div className="flex items-center gap-2 flex-wrap">
                                  {tagList.map((t) => (
                                    <span
                                      key={t}
                                      className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded"
                                    >
                                      {t}
                                    </span>
                                  ))}
                                  {note?.notes && (
                                    <span className="text-xs text-gray-500 truncate max-w-[120px]" title={note.notes}>
                                      {note.notes}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openNoteEditor(customer.email)}
                                    className="p-1 text-gray-400 hover:text-purple-600 rounded"
                                    title="Edit notes & tags"
                                  >
                                    <HiPencil className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            })()}
                          </>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-medium mb-2">💡 Growing Your Customer Base</p>
        <ul className="space-y-1">
          <li>• Share your website on Facebook and Instagram</li>
          <li>• Ask customers to sign up for email updates at pickup</li>
          <li>• Offer a small discount for email subscribers</li>
        </ul>
      </div>
    </div>
  );
}
